import React from 'react';

interface StickyFooterProps {
  /**
   * The content to be displayed inside the sticky footer.
   * This can be Button components, pricing information, or any other content.
   */
  children: React.ReactNode;
  /**
   * Additional CSS classes to apply to the footer container
   */
  className?: string;
  /**
   * Z-index value for the footer. Defaults to 50.
   */
  zIndex?: number;
}

/**
 * StickyFooter Component
 *
 * A flexible sticky footer component that stays fixed at the bottom of the screen.
 * Features rounded top corners and a subtle shadow to match the design system.
 *
 * Key Features:
 * - Fixed positioning at the bottom of the viewport
 * - Rounded top corners (24px radius)
 * - Subtle top shadow for depth
 * - Flexible content area - accepts any children
 * - Mobile-first responsive design
 * - Consistent padding and spacing
 *
 * Common Use Cases:
 * - Action buttons (Next, Submit, etc.)
 * - Pricing summaries with action buttons
 * - Multi-button layouts
 * - Complex content with multiple sections
 *
 * @example
 * // Simple button footer
 * <StickyFooter>
 *   <Button onClick={handleNext}>Next</Button>
 * </StickyFooter>
 *
 * @example
 * // Pricing summary with button
 * <StickyFooter>
 *   <div className="space-y-2">
 *     <div className="flex justify-between items-center">
 *       <span className="font-semibold text-xl">Total</span>
 *       <span className="text-xl font-bold">฿900</span>
 *     </div>
 *     <Button onClick={handleConfirm} className="w-full">
 *       Confirm Payment
 *     </Button>
 *   </div>
 * </StickyFooter>
 *
 * @example
 * // Multiple buttons
 * <StickyFooter>
 *   <div className="flex gap-3">
 *     <Button variant="secondary" onClick={handleBack}>
 *       Back
 *     </Button>
 *     <Button onClick={handleNext} className="flex-1">
 *       Next
 *     </Button>
 *   </div>
 * </StickyFooter>
 */
const StickyFooter: React.FC<StickyFooterProps> = ({
  children,
  className = '',
  zIndex = 50,
}) => {
  return (
    <div
      className={`shadow-footer fixed right-0 bottom-0 left-0 rounded-tl-[24px] rounded-tr-[24px] bg-white px-4 pt-4 pb-6 ${className} `}
      style={{ zIndex }}
    >
      <div className="mx-auto max-w-2xl">{children}</div>
    </div>
  );
};

export default StickyFooter;
